import { QueryClient, useMutation } from 'react-query';
import { showNotification, hideNotification } from '@mantine/notifications';
import { deleteEntity } from '../../../../../services/request/assets';
import { useIntlLight } from '../../../../i18n';

export interface IUseCrudMulti {
  queryKey: string;
  queryClient: QueryClient;
}

const useMultiDelete = ({ queryKey, queryClient }: IUseCrudMulti) => {
  const deleteMultiMutation = useMutation(deleteEntity);
  const { t } = useIntlLight();

  const handlerDelete = async (rowChecked: string[]): Promise<any> => {
    const total = rowChecked.length;
    let countSuccess = 0;
    const newRowChecked: string[] = [];
    const queue = [...rowChecked];
    const MAX_CONCURRENT = 10;
    const notificationId = 'batch-delete-progress';

    if (total > 0) {
      showNotification({
        id: notificationId,
        title: t('systemBase.showNotification.systemMessageLabel'),
        message: t('all_notification_batchDelete_inProgress'),
        loading: true,
        autoClose: false,
        disallowClose: true,
      });
    }

    const worker = async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) break;
        try {
          // eslint-disable-next-line no-await-in-loop
          await deleteMultiMutation.mutateAsync(item);
          countSuccess += 1;
        } catch (error) {
          newRowChecked.push(item);
        }
      }
    };

    const workers = [];
    for (let i = 0; i < Math.min(MAX_CONCURRENT, total); i += 1) {
      workers.push(worker());
    }

    await Promise.all(workers);

    if (total > 0) {
      hideNotification(notificationId);
    }

    if (countSuccess > 0) {
      showNotification({
        title: t('systemBase.showNotification.systemMessageLabel'),
        message: t('all_notification_batchDelete', { count: countSuccess }),
        color: 'green',
      });
      queryClient.invalidateQueries(queryKey).then(() => null);
    }

    if (newRowChecked.length > 0) {
      showNotification({
        title: t('systemBase.showNotification.systemMessageLabel'),
        message: t('all_notification_batchDeleteFailure', { count: newRowChecked.length }),
        color: 'red',
      });
    }

    return newRowChecked;
  };

  return { handlerDelete };
};

export default useMultiDelete;
